import React, { JSX, useRef, useState } from "react";
import ReactDOM from "react-dom";

import useClickAway from "hooks/use-outside-click";
import { Icon } from "components/icon/Icon";

import "./Tooltip.scss";

export enum TooltipAction {
  Click,
  Hover
}

export type TooltipProps = {
  content: JSX.Element;
  children: JSX.Element;
  delay?: number;
  container?: HTMLElement;
  enabled?: boolean;
  showOnAction?: TooltipAction;
} & Omit<React.HTMLProps<HTMLDivElement>, "content">;

export type Position = {
  x: number;
  y: number;
};

export function Tooltip({
  content,
  children,
  style,
  enabled = true,
  className = "",
  container = document.body,
  showOnAction = TooltipAction.Hover
}: TooltipProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<Position>({
    x: -1,
    y: -1
  });

  useClickAway(tooltip, () => {
    setActive(false);
  });

  const updateTooltipPosition = () => {
    if (!tooltip.current || !wrapper.current) {
      return;
    }

    const containerWidth = container.getBoundingClientRect().width;
    const containerHeight = container.getBoundingClientRect().height;
    const tooltipHeight = tooltip.current.getBoundingClientRect().height;
    const tooltipWidth = tooltip.current.getBoundingClientRect().width;
    const wrapperWidth = wrapper.current.getBoundingClientRect().width;

    const tooltipX = Math.min(
      containerWidth - tooltipWidth / 2,
      wrapper.current.getBoundingClientRect().left + wrapperWidth / 2
    );

    const tooltipY =
      Math.min(containerHeight, wrapper.current.getBoundingClientRect().top) -
      tooltipHeight -
      10;

    setTooltipPosition({
      x: tooltipX,
      y: tooltipY
    });
  };

  const setTooltipActive = () => {
    updateTooltipPosition();
    setActive(true);
  };

  const onMouseEnter = () => {
    if (showOnAction === TooltipAction.Hover) {
      setTooltipActive();
    }
  };

  const onMouseLeave = () => {
    if (showOnAction === TooltipAction.Hover) {
      setActive(false);
    }
  };

  const onClick = () => {
    if (showOnAction === TooltipAction.Click) {
      setTooltipActive();
    }
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={wrapper}
        className={`tooltip-wrapper ${className}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {children}
      </div>

      {ReactDOM.createPortal(
        <div
          className="tooltip"
          style={{
            left: Math.abs(tooltipPosition.x),
            top: Math.abs(tooltipPosition.y),
            visibility: active ? "visible" : "hidden",
            ...style
          }}
        >
          <div
            ref={tooltip}
            className="tooltip-content"
            style={{
              pointerEvents:
                showOnAction === TooltipAction.Hover ? "none" : "auto"
            }}
          >
            {showOnAction === TooltipAction.Click && (
              <div className={"d-flex justify-content-end"}>
                <Icon name={"remove"} onClick={() => setActive(false)} />
              </div>
            )}
            {content}
          </div>
          <div
            style={{ display: tooltipPosition.y > 0 ? "block" : "none" }}
            className="tooltip-tip"
          ></div>
        </div>,
        document.body
      )}
    </>
  );
}
